import { Component } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';

import { Profile, WhereClause } from 'src/models';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { UsersService } from 'src/services/users.service';

@Component({
  selector: 'app-merge-users-modal',
  templateUrl: './merge-users-modal.component.html',
  styleUrls: ['./merge-users-modal.component.scss'],
})
export class MergeUsersModalComponent {
  public selectedSourceUser: Partial<Profile> | null = null;
  public selectedTargetUser: Partial<Profile> | null = null;
  public isSubmitting = false;

  constructor(
    private modalCtrl: ModalController,
    private usersService: UsersService,
    private msgService: MessageService,
    private alertCtrl: AlertController,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async selectSourceUser(event: any) {
    this.searchablePopoverService.users({
      callback: (id: string, name: string, user: any) => {
        this.selectedSourceUser = user;
        this.selectedTargetUser = null;
      },
      event,
      showNoneOption: false,
      showCurrentUserOption: false,
      includeExternalUsers: true,
      includeEmailOnlyUsers: false,
    });
  }

  async selectTargetUser(event: any) {
    if (!this.selectedSourceUser) {
      this.msgService.showBottom('Please select a source user first');
      return;
    }

    // Create where clause to filter users based on source user type
    const where: WhereClause[] = [];

    // If source user is external, only show external users for target
    // If source user is internal, only show internal users for target
    if (this.selectedSourceUser.isExternal) {
      where.push({
        field: 'isExternal',
        operator: '==',
        value: true,
      });
    } else {
      where.push({
        field: 'isExternal',
        operator: '!=',
        value: true,
      });
    }

    // Exclude the selected source user from target options
    where.push({
      field: 'id',
      operator: '!=',
      value: this.selectedSourceUser.id,
    });

    this.searchablePopoverService.users({
      callback: (id: string, name: string, user: any) => {
        this.selectedTargetUser = user;
      },
      event,
      showNoneOption: false,
      showCurrentUserOption: false,
      includeExternalUsers: true,
      includeEmailOnlyUsers: false,
      where,
    });
  }

  clearSourceUser() {
    this.selectedSourceUser = null;
    this.selectedTargetUser = null; // Clear target when source is cleared
  }

  clearTargetUser() {
    this.selectedTargetUser = null;
  }

  canSubmit(): boolean {
    return Boolean(this.selectedSourceUser && this.selectedTargetUser && !this.isSubmitting);
  }

  async confirmMerge() {
    if (!this.canSubmit()) return;

    const alert = await this.alertCtrl.create({
      header: '⚠️ Confirm User Merge',
      subHeader: 'This action cannot be undone!',
      message: `
        <p><strong>${this.selectedSourceUser!.name}</strong> will be deleted and all data transferred to <strong>${this.selectedTargetUser!.name}</strong>.</p>
        <br>
        <p><strong>Are you sure you want to proceed?</strong></p>
      `,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Merge Users',
          role: 'destructive',
          handler: async () => {
            await this.performMerge();
          },
        },
      ],
    });

    await alert.present();
  }

  private async performMerge() {
    this.isSubmitting = true;
    try {
      await this.usersService.mergeUsers(this.selectedSourceUser!.id!, this.selectedTargetUser!.id!);
      this.msgService.showBottom('✔︎ Users merged successfully');
      this.modalCtrl.dismiss({ merged: true });
    } catch (error: any) {
      this.msgService.showBottom(`Error merging users: ${error.message}`);
    } finally {
      this.isSubmitting = false;
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  formatUserName(user: Partial<Profile>) {
    let result = user.name?.trim() || user.email?.trim() || 'Unknown';

    if (user.email?.trim() && user.email?.trim() !== result) {
      result += ` (${user.email})`;
    }

    return result;
  }
}
